import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { useTranslation, untranslated, type TranslationKey } from "@i18n";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import SlickGrid1, { typedColumns } from "@CubeComponents/html/SlickGrid1";
import IconComponent from "@CubeComponents/icons";
import Text from "@CubeComponents/html/Text/Text";
import { getExternalLinkStats, type ExternalLinkListItem } from "./externalLinks.api";
import { LINK_DIALOG_WIDTH, formatGridTimestamp } from "./externalLinks.helpers";
import type { GetExternalLinkStatsResponse } from "@ServerTypes/externalLinks/externalLinks.schema";

/**
 * "View Stats" — the link's full access timeline.
 *
 * A real event list rather than counters: every view and download with its timestamp and IP where
 * available, so an owner can answer "who opened this, and when" from one screen.
 */
type ExternalLinkStatsModalProps = {
  link: ExternalLinkListItem;
  onClose: () => void;
};

type StatsEvent = NonNullable<Extract<GetExternalLinkStatsResponse, { status: true }>["events"]>[number];

/** The grid needs a stable `id` per row; SlickGrid keys everything on it. */
type StatsGridRow = StatsEvent & { id: string };

type EventPresentation = { icon: string; labelKey: TranslationKey };

/**
 * Event type → icon + label.
 *
 * `satisfies` the SERVER'S event union, not just `string`: adding a type to `EXTERNAL_LINK_EVENT_VALUES`
 * becomes a compile error here rather than a row that renders its raw enum value.
 *
 * A LOOKUP still yields `| undefined`, deliberately: `getExternalLinkStats` types the response off the
 * server's Zod schema but nothing re-validates it in the browser, so a type added server-side reaches an
 * older bundle as a value outside the union. `labelKey` is typed `TranslationKey` so a label missing from
 * en.json is also a compile error.
 */
const EVENT_PRESENTATION: Record<string, EventPresentation | undefined> = {
  viewed: { icon: "EyeOpen", labelKey: "Viewed" },
  downloaded: { icon: "download", labelKey: "Downloaded" },
  link_edited: { icon: "editProfile", labelKey: "Link edited" },
  password_changed: { icon: "Lock", labelKey: "Password changed" },
  created: { icon: "Link", labelKey: "Link created" },
  revoked: { icon: "delete", labelKey: "Link revoked" },
  reinstated: { icon: "refresh", labelKey: "Link enabled" },
  disabled_by_admin: { icon: "Lock", labelKey: "Disabled by admin" },
} satisfies Record<StatsEvent["type"], EventPresentation>;

/** SlickGrid formatters return DOM, not JSX — the standard detached-root bridge. */
const renderInContainer = (element: ReactNode) => {
  const container = document.createElement("div");
  createRoot(container).render(element);
  return container;
};

function ExternalLinkStatsModal({ link, onClose }: Readonly<ExternalLinkStatsModalProps>) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<StatsEvent[]>([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const res = await getExternalLinkStats({
        linkId: link._id,
        // PRD 3.7 verbatim: "Only Viewed and Downloaded events are shown; configuration changes are
        // out of scope for this view." Filtered SERVER-side so the rule holds past one page.
        types: ["viewed", "downloaded"],
        take: 200,
      });
      if (cancelled) return;
      if (res.status) {
        setEvents(res.events ?? []);
      }
      setLoading(false);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [link._id]);

  const gridRows = useMemo<StatsGridRow[]>(() => events.map((event) => ({ ...event, id: event._id })), [events]);

  /**
   * The standard grid (QA r2 #10), replacing the hand-rolled table AND its bespoke expanding search:
   * SlickGrid1's own search bar covers it, matching on the raw event type ("downloaded" matches
   * "download") and the IP. Memoised — a fresh columns array every render re-seeds the grid.
   */
  const columnDefinitions = useMemo(() => {
    const eventCell = (event: StatsGridRow) => {
      const presentation = EVENT_PRESENTATION[event.type];
      return (
        <span className="flex min-w-0 items-center gap-2">
          <IconComponent name={presentation?.icon ?? "info"} width="1rem" height="1rem" />
          <span className="truncate">
            {/* Server event types are a closed set; anything unmapped is shown raw rather than blank
                so a new type is visible instead of silently missing. */}
            {presentation ? t(presentation.labelKey) : event.type}
          </span>
          {!!event.actorName && <span className="truncate text-xs text-text_light">{`· ${event.actorName}`}</span>}
        </span>
      );
    };
    return typedColumns<StatsGridRow>([
      {
        id: "type",
        field: "type",
        name: t("Event Type"),
        minWidth: 180,
        sortable: true,
        filterType: "Text",
        formatter: (_data: number, _params: number, _a: unknown, _b: unknown, event: StatsGridRow) =>
          renderInContainer(eventCell(event)),
      },
      {
        // QA r8 #3: WHICH file/folder the event touched, as its own column (it was inline text
        // beside the event type). The server logs it as `detail` — the resolved item's name on
        // viewed/downloaded — and it is in `searchFields` below.
        id: "detail",
        field: "detail",
        name: t("File / Folder"),
        minWidth: 200,
        sortable: true,
        filterType: "Text",
        formatter: (_data: number, _params: number, _a: unknown, _b: unknown, event: StatsGridRow) =>
          event.detail ?? "—",
        _valueGetter: (event: StatsGridRow) => event.detail ?? "",
      },
      {
        id: "at",
        field: "at",
        name: t("Date & Time"),
        minWidth: 170,
        sortable: true,
        formatter: (_data: number, _params: number, _a: unknown, _b: unknown, event: StatsGridRow) =>
          formatGridTimestamp(event.at),
        _valueGetter: (event: StatsGridRow) => event.at,
      },
      {
        id: "ip",
        field: "ip",
        name: t("IP Address"),
        minWidth: 120,
        sortable: true,
        // "—" rather than blank: FR-27 records IP "where available", and creator-side events
        // genuinely have none.
        formatter: (_data: number, _params: number, _a: unknown, _b: unknown, event: StatsGridRow) => event.ip ?? "—",
        _valueGetter: (event: StatsGridRow) => event.ip ?? "",
      },
    ]);
  }, [t]);

  return (
    <ModalStateDialogComponent
      isVisible
      width={LINK_DIALOG_WIDTH}
      headerTitle={
        <div className="flex min-w-0 items-center gap-2">
          {/* Back, not only X: this dialog is reached FROM Manage, and the prototype returns there. */}
          <IconComponent name="ArrowLeft" width="1rem" height="1rem" onClick={onClose} dataTestId="statsBack" />
          <Text className="truncate">{t("Link stats")}</Text>
          <Text className="truncate text-sm font-normal text-text_light">{untranslated(`— ${link.name}`)}</Text>
        </div>
      }
      onCancel={onClose}
      modelContent={
        <div className="h-96 py-2">
          {loading ? (
            <div className="px-3 py-8 text-center text-sm text-text_light">{t("Loading...")}</div>
          ) : (
            <SlickGrid1
              dataset={gridRows}
              columnDefinitions={columnDefinitions}
              disableCheckBoxSelection
              searchFields={["type", "ip", "detail"]}
              searchPlaceholder={t("Search")}
              rowUnitLabel={t("events")}
              disableMenuOptions
            />
          )}
        </div>
      }
    />
  );
}

export default ExternalLinkStatsModal;
